import React, { useMemo, useRef, useState, useEffect } from "react";
import performRequest from "../../api/request";
import { Toast } from "../../CustomComponent/toast";
import { CustomErrorResponse } from "../../CustomComponent/custom_error_response";
import { useNavigate } from "react-router-dom";
// Polyhouse Foundation Layout Generator
// - Inputs: Length & Width (m), Hockey buffer (m)
// - Fixed spacings: Bay = 4 m (X), Column = 8 m (Y)
// - BC (Big Column) points: perimeter-only footings midway between 8 m column lines on vertical edges
// - HC (Hockey) points: outer hockey-border points every 4 m, starting 2 m from each corner, with corner join lines
// - Entry Cabin: 4 m × 4 m placed between HCn–HC(n+1), **centered on the hockey border** (2 m out + 2 m in)
// - Blocks: label every full 4×8 m bay-rectangle; draw a border around each block
// - Assign each block a **System** (Flatbed or Growbag) by percentage; allocation stays **contiguous** in a serpentine path.
// - Flatbed blocks now show two internal tables of 6.5 m × 1.2 m, parallel and equally spaced inside the 4×8 block, with 12 internal 7.0 m lines per table (clipped to table).
// - Growbag blocks now show 12 horizontal rows (4.0 m long) arranged as 6 pairs, with even spacing between pairs. Each row renders 13 round growbag pots.

export default function PolyhouseFoundationDesigner({
  setHCPairs,
  BASE_SCALE,
  length,
  width,
  entryStartLabel,
  footingDiaM,
  precision,
  showGrid,
  showLabels,
  showOutline,
  showBC,
  showHC,
  growPct,
  dutchPct,
  aframePct,
  flatbedPct,
  blockName,
  designOpn,
  // clientName,
  role,
}) {
  // Inputs (meters)=

  // const [lengthM, setLengthM] = useState(40); // along X
  // const [widthM, setWidthM] = useState(24); // along Y
  const lengthM = length - 4;
  const widthM = width - 4;
  const BAY_SPACING = 4; // fixed per request
  const COL_SPACING = 8; // fixed per request
  const bufferM = 2; // hockey area buffer on all 4 sides (m)

  // System allocation (exact combos requested)
  const svgRef = useRef(null);
  const navigate = useNavigate();

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const EPS = 1e-6;
  const eq = (a, b) => Math.abs(a - b) < EPS;

  function buildAxisPositions(total, spacing) {
    const pts = [];
    let x = 0;
    const eps = 1e-9;
    while (x < total - eps) {
      pts.push(Number(x.toFixed(6)));
      x += spacing;
    }
    if (pts.length === 0 || Math.abs(pts[pts.length - 1] - total) > eps) {
      pts.push(Number(total.toFixed(6)));
    }
    return pts;
  }

  function buildInclusive(start, end, step) {
    const pts = [];
    const dir = Math.sign(end - start) || 1;
    const s = step * dir;
    const eps = 1e-9;
    if (dir > 0) {
      for (let v = start; v <= end + eps; v += s)
        pts.push(Number(v.toFixed(6)));
    } else {
      for (let v = start; v >= end - eps; v += s)
        pts.push(Number(v.toFixed(6)));
    }
    return pts;
  }

  const toLetters = (idx) => {
    let n = idx;
    let s = "";
    while (n >= 0) {
      s = String.fromCharCode((n % 26) + 65) + s;
      n = Math.floor(n / 26) - 1;
    }
    return s;
  };

  const { xs, ys, points, meta } = useMemo(() => {
    const L = Math.max(0.5, Number(lengthM) || 0);
    const W = Math.max(0.5, Number(widthM) || 0);
    const sx = BAY_SPACING;
    const sy = COL_SPACING;

    const xs = buildAxisPositions(L, sx);
    const ys = buildAxisPositions(W, sy);

    const pts = [];
    ys.forEach((y, yi) => {
      xs.forEach((x, xi) => {
        const row = toLetters(yi);
        const col = xi + 1;
        pts.push({ label: `${row}${col}`, x, y, col, row });
      });
    });

    const meta = {
      bayCount: xs.length,
      colLineCount: ys.length,
      liveBaySpacing: xs.length > 1 ? xs[1] - xs[0] : 0,
      liveColSpacing: ys.length > 1 ? ys[1] - ys[0] : 0,
      diagM: Math.hypot(L, W),
      L,
      W,
    };

    return { xs, ys, points: pts, meta };
  }, [lengthM, widthM]);

  const { bcPoints, hcPoints, hcPairs, blocks, bRows, bCols } = useMemo(() => {
    // --- BC: vertical edges, midpoints between 8 m column lines ---
    const stepY = meta.liveColSpacing || COL_SPACING; // 8 m
    const mid = stepY / 2; // 4 m
    const bc = [];
    let bcIdx = 1;
    if (mid > 0 && meta.W - mid >= mid) {
      const ysMid = buildInclusive(mid, meta.W - mid, stepY);
      for (const y of ysMid) {
        for (const xEdge of [0, meta.L]) {
          bc.push({ x: xEdge, y, label: `BC${bcIdx++}`, kind: "BC" });
        }
      }
    }

    // --- HC: hockey-border perimeter, every 4 m, starting 2 m from each corner ---
    const b = Number(bufferM) || 0;
    const stepHC = 4; // meters
    const off = 2; // 2 m from corners
    const hc = [];
    for (const x of buildInclusive(-b + off, meta.L + b - off, stepHC))
      hc.push({ x, y: -b, label: "" });
    for (const x of buildInclusive(-b + off, meta.L + b - off, stepHC))
      hc.push({ x, y: meta.W + b, label: "" });
    for (const y of buildInclusive(-b + off, meta.W + b - off, stepHC))
      hc.push({ x: -b, y, label: "" });
    for (const y of buildInclusive(-b + off, meta.W + b - off, stepHC))
      hc.push({ x: meta.L + b, y, label: "" });
    hc.forEach((p, i) => (p.label = `HC${i + 1}`));

    // Build valid consecutive pairs (same edge only)
    const hcPairs = [];
    const eqN = (a, b) => Math.abs(a - b) < 1e-6;
    for (let i = 0; i < hc.length - 1; i++) {
      const a = hc[i],
        bpt = hc[i + 1];
      if (eqN(a.y, -b) && eqN(bpt.y, -b) && !eqN(a.x, bpt.x))
        hcPairs.push({ startLabel: a.label, i, j: i + 1, edge: "bottom" });
      if (eqN(a.y, meta.W + b) && eqN(bpt.y, meta.W + b) && !eqN(a.x, bpt.x))
        hcPairs.push({ startLabel: a.label, i, j: i + 1, edge: "top" });
      if (eqN(a.x, -b) && eqN(bpt.x, -b) && !eqN(a.y, bpt.y))
        hcPairs.push({ startLabel: a.label, i, j: i + 1, edge: "left" });
      if (eqN(a.x, meta.L + b) && eqN(bpt.x, meta.L + b) && !eqN(a.y, bpt.y))
        hcPairs.push({ startLabel: a.label, i, j: i + 1, edge: "right" });
    }

    // --- Blocks: one box per full 4×8 cell ---
    const blocks = [];
    let bRows = 0,
      bCols = 0;
    for (let yi = 0; yi < ys.length - 1; yi++) {
      const y0 = ys[yi],
        y1 = ys[yi + 1];
      if (!eq(y1 - y0, COL_SPACING)) continue; // require full 8 m height
      let colsThisRow = 0;
      for (let xi = 0; xi < xs.length - 1; xi++) {
        const x0 = xs[xi],
          x1 = xs[xi + 1];
        if (!eq(x1 - x0, BAY_SPACING)) continue; // require full 4 m width
        const name = `Block ${toLetters(yi)}${xi + 1}`;
        blocks.push({
          x: (x0 + x1) / 2,
          y: (y0 + y1) / 2,
          name,
          x0,
          y0,
          w: BAY_SPACING,
          h: COL_SPACING,
          xi,
          yi,
        });
        colsThisRow++;
      }
      if (colsThisRow > 0) {
        bRows++;
        if (bCols === 0) bCols = colsThisRow;
      }
    }

    return { bcPoints: bc, hcPoints: hc, hcPairs, blocks, bRows, bCols };
  }, [xs, ys, meta.L, meta.W, meta.liveColSpacing, bufferM]);

  const selectedPair = useMemo(
    () => hcPairs.find((p) => p.startLabel === entryStartLabel),
    [hcPairs, entryStartLabel]
  );

  // Entry Cabin rect (centered on border: 2 m out + 2 m in)
  function getEntryRect() {
    if (!selectedPair) return null;
    const a = hcPoints[selectedPair.i];
    const b = hcPoints[selectedPair.j];
    if (!a || !b) return null;

    if (eq(a.y, b.y)) {
      const yEdge = a.y; // -b (bottom) or W+b (top)
      const x0 = Math.min(a.x, b.x);
      const w = Math.abs(b.x - a.x); // 4 m
      const h = 4;
      const y0 = yEdge - 2; // center around border
      return { x0, y0, w, h };
    } else if (eq(a.x, b.x)) {
      const xEdge = a.x; // -b (left) or L+b (right)
      const y0 = Math.min(a.y, b.y);
      const h = Math.abs(b.y - a.y); // 4 m
      const w = 4;
      const x0 = xEdge - 2; // center around border
      return { x0, y0, w, h };
    }
    return null;
  }
  const entryRect = getEntryRect();

  // --- Serpentine order for contiguity ---
  // const serpentineOrder = useMemo(() => {
  //   // Build an array of block indices ordered in a snake (left→right, then right→left)
  //   const indexByPos = new Map();
  //   blocks.forEach((b, idx) => indexByPos.set(`${b.xi},${b.yi}`, idx));
  //   const order = [];
  //   for (let yi = 0; yi < bRows; yi++) {
  //     if (yi % 2 === 0) {
  //       for (let xi = 0; xi < bCols; xi++) {
  //         const idx = indexByPos.get(`${xi},${yi}`);
  //         if (idx !== undefined) order.push(idx);
  //       }
  //     } else {
  //       for (let xi = bCols - 1; xi >= 0; xi--) {
  //         const idx = indexByPos.get(`${xi},${yi}`);
  //         if (idx !== undefined) order.push(idx);
  //       }
  //     }
  //   }
  //   return order;
  // }, [blocks, bRows, bCols]);

  // --- Serpentine order for contiguity (TOP → BOTTOM, column-wise) ---
  // const serpentineOrder = useMemo(() => {
  //   const indexByPos = new Map();
  //   blocks.forEach((b, idx) => indexByPos.set(`${b.xi},${b.yi}`, idx));

  //   const order = [];

  //   // iterate columns first (LEFT → RIGHT)
  //   for (let xi = 0; xi < bCols; xi++) {
  //     if (xi % 2 === 0) {
  //       // TOP → BOTTOM
  //       for (let yi = bRows - 1; yi >= 0; yi--) {
  //         const idx = indexByPos.get(`${xi},${yi}`);
  //         if (idx !== undefined) order.push(idx);
  //       }
  //     } else {
  //       // BOTTOM → TOP
  //       for (let yi = 0; yi < bRows; yi++) {
  //         const idx = indexByPos.get(`${xi},${yi}`);
  //         if (idx !== undefined) order.push(idx);
  //       }
  //     }
  //   }

  //   return order;
  // }, [blocks, bRows, bCols]);

  const serpentineOrder = useMemo(() => {
    const indexByPos = new Map();
    blocks.forEach((b, idx) => indexByPos.set(`${b.xi},${b.yi}`, idx));

    const order = [];

    if (designOpn === "opn1") {
      // ROW-WISE serpentine (LEFT → RIGHT, then RIGHT → LEFT)
      for (let yi = 0; yi < bRows; yi++) {
        if (yi % 2 === 0) {
          for (let xi = 0; xi < bCols; xi++) {
            const idx = indexByPos.get(`${xi},${yi}`);
            if (idx !== undefined) order.push(idx);
          }
        } else {
          for (let xi = bCols - 1; xi >= 0; xi--) {
            const idx = indexByPos.get(`${xi},${yi}`);
            if (idx !== undefined) order.push(idx);
          }
        }
      }
    } else if (designOpn === "opn2") {
      // COLUMN-WISE serpentine (TOP → BOTTOM, then BOTTOM → TOP)
      for (let xi = 0; xi < bCols; xi++) {
        if (xi % 2 === 0) {
          for (let yi = bRows - 1; yi >= 0; yi--) {
            const idx = indexByPos.get(`${xi},${yi}`);
            if (idx !== undefined) order.push(idx);
          }
        } else {
          for (let yi = 0; yi < bRows; yi++) {
            const idx = indexByPos.get(`${xi},${yi}`);
            if (idx !== undefined) order.push(idx);
          }
        }
      }
    }

    return order;
  }, [designOpn, blocks, bRows, bCols]);

  // --- Allocation (Flatbed vs Growbag) ---
  const assignment = useMemo(() => {
    const total = blocks.length;
    const flatCount = Math.floor((clamp(flatbedPct, 0, 100) / 100) * total);
    const aframeCount = Math.floor((clamp(aframePct, 0, 100) / 100) * total);
    const dutchCount = Math.floor((clamp(dutchPct, 0, 100) / 100) * total);
    const growCount = Math.floor((clamp(growPct, 0, 100) / 100) * total);
    // Remaining blocks (if any) will be growbag

    const setFlat = new Set();
    const setAFrame = new Set();
    const setDutch = new Set();

    serpentineOrder.forEach((idx, i) => {
      if (i < flatCount) setFlat.add(idx);
      else if (i < flatCount + aframeCount) setAFrame.add(idx);
      else if (i < flatCount + aframeCount + dutchCount) setDutch.add(idx);
    });

    return {
      flatCount,
      aframeCount,
      dutchCount,
      growCount,
      typeOf: (i) =>
        setFlat.has(i)
          ? "flat"
          : setAFrame.has(i)
          ? "aframe"
          : setDutch.has(i)
          ? "dutch"
          : "growbag",
    };
  }, [
    blocks.length,
    serpentineOrder,
    flatbedPct,
    aframePct,
    growPct,
    dutchPct,
  ]);

  // ====== Flatbed internal tables geometry (6.5 × 1.2 m) + internal lines ======
  const TABLE_L = 6.5; // along the 8 m direction (Y axis)
  const TABLE_W = 1.2; // along the 4 m direction (X axis)
  const LINES_PER_TABLE = 12;
  const LINE_LEN = 7.0; // requested 7 m vertical lines (will be clipped to the 6.5 m table)

  function computeTablesInBlock(b) {
    // Two tables placed side-by-side across the 4 m width, equal gaps: left, middle, right
    const totalW = b.w; // 4 m
    const usedW = 2 * TABLE_W; // 2.4 m
    const gapX = (totalW - usedW) / 3; // ~0.533333 m (equal left/middle/right gaps)

    // Vertical gaps (top & bottom), center 6.5 m inside 8 m
    const totalH = b.h; // 8 m
    const gapY = (totalH - TABLE_L) / 2; // 0.75 m each

    // Rect 1 (left)
    const r1 = { x0: b.x0 + gapX, y0: b.y0 + gapY, w: TABLE_W, h: TABLE_L };
    // Rect 2 (right)
    const r2 = {
      x0: b.x0 + gapX + TABLE_W + gapX,
      y0: b.y0 + gapY,
      w: TABLE_W,
      h: TABLE_L,
    };

    return { r1, r2, gapX, gapY };
  }

  function computeLineXs(x0, w, count) {
    // Evenly spaced across width, leaving equal margin on both sides.
    const xs = [];
    const step = w / (count + 1);
    for (let i = 0; i < count; i++) xs.push(x0 + step * (i + 1));
    return { xs, step };
  }

  // ===== Growbag rows (12 rows in 6 pairs), each row 4 m long -> render as round pots =====
  const GB_PAIRS = 6; // 12 lines total
  const GB_LINES_PER_PAIR = 2;
  const GB_LINE_LEN = 4.0; // meters (span full block width)
  const GB_INTRA_PAIR_GAP = 0.25; // meters between the two lines inside a pair
  const GB_POTS_PER_LINE = 13; // round growbags per 4 m line
  const GB_POT_D = 0.25; // meters diameter (visual only)
  const DB_POT_D = 0.25; // meters diameter (visual only)

  function computeGrowbagLineYs(b) {
    // Lines are horizontal (length = 4 m) across the block width, placed along Y in pairs.
    const pairs = GB_PAIRS;
    const gapCount = pairs + 1; // top gap, gaps between pairs, bottom gap
    const totalPairThickness = pairs * GB_INTRA_PAIR_GAP; // each pair occupies this vertical thickness between its two lines
    const gap = (b.h - totalPairThickness) / gapCount; // even spacing between pairs
    const ys = [];
    for (let p = 0; p < pairs; p++) {
      const yFirst = b.y0 + gap + p * (GB_INTRA_PAIR_GAP + gap);
      ys.push(yFirst, yFirst + GB_INTRA_PAIR_GAP);
    }
    return { ys, gap };
  }

  // Canvas sizing & scaling (100% width + scroll when needed)
  const pad = 30; // px
  const { scale, canvasPx, minXM, minYM, innerHPx } = useMemo(() => {
    let extraLeftM = 0,
      extraRightM = 0,
      extraTopM = 0,
      extraBottomM = 0;
    if (selectedPair) {
      if (selectedPair.edge === "left") extraLeftM = 2;
      if (selectedPair.edge === "right") extraRightM = 2;
      if (selectedPair.edge === "top") extraTopM = 2;
      if (selectedPair.edge === "bottom") extraBottomM = 2;
    }
    const minXM = -bufferM - extraLeftM;
    const minYM = -bufferM - extraBottomM;
    const Ld = lengthM + 2 * bufferM + extraLeftM + extraRightM;
    const Wd = widthM + 2 * bufferM + extraTopM + extraBottomM;
    const scale = BASE_SCALE;
    const innerWPx = Ld * scale;
    const innerHPx = Wd * scale;
    const canvasPx = {
      w: Math.round(innerWPx + 2 * pad),
      h: Math.round(innerHPx + 2 * pad),
    };
    return { scale, canvasPx, minXM, minYM, innerHPx };
  }, [lengthM, widthM, bufferM, selectedPair, BASE_SCALE]);

  function mx(xm) {
    return pad + (xm - minXM) * scale;
  }
  function my(ym) {
    return pad + (innerHPx - (ym - minYM) * scale);
  }

  // function downloadSVG() {
  //   const svg = svgRef.current;
  //   if (!svg) return;
  //   const serializer = new XMLSerializer();
  //   const source = serializer.serializeToString(svg);
  //   const blob = new Blob(
  //     [`<?xml version="1.0" encoding="UTF-8"?>\n` + source],
  //     { type: "image/svg+xml;charset=utf-8" }
  //   );
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = `polyhouse-foundation.svg`;
  //   a.click();
  //   URL.revokeObjectURL(url);
  // }

  const [clientName, setClientName] = useState();

  function calculateSystemSize(blocks, assignment) {
    function getSize(type) {
      const filtered = blocks.filter((_, i) => assignment.typeOf(i) === type);

      if (!filtered.length) {
        return { width: 0, length: 0, blocks: 0 };
      }

      const minX = Math.min(...filtered.map((b) => b.x0));
      const maxX = Math.max(...filtered.map((b) => b.x0 + b.w));
      const minY = Math.min(...filtered.map((b) => b.y0));
      const maxY = Math.max(...filtered.map((b) => b.y0 + b.h));

      // return {
      //   width: maxX - minX + 4,
      //   length: maxY - minY + 4,
      //   blocks: filtered.length,
      // };

      let width = maxX - minX + 4;
      let length = maxY - minY + 4;

      if ((length - 4) % 8 === 0) {
        return {
          width: length,
          length: width,
          blocks: filtered.length,
        };
      }

      return {
        width,
        length,
        blocks: filtered.length,
      };
    }

    let data = {
      flatbed: getSize("flat"),
      growbag: getSize("growbag"),
    };

    performRequest(
      "GET",
      `/materialPlanning/calculateCombofGowandFlat?flatbed=${encodeURIComponent(
        JSON.stringify(data.flatbed)
      )}&growbag=${encodeURIComponent(
        JSON.stringify(data.growbag)
      )}&length=${encodeURIComponent(length)}&width=${encodeURIComponent(width)}
      &flatbedPercentage=${encodeURIComponent(
        flatbedPct
      )}&growbagPercentage=${encodeURIComponent(growPct)}
      `
    )
      .then((res) => {
        if (res.status === 200) {
          navigate("/material-list-flat-grow/table", {
            state: {
              project: clientName,
              mergedList: res.data.CombaineList,
              hydrponicType: "hydroponicsOnly",
              length: res.data.length,
              width: res.data.width,
            },
          });

          let btnClose = document.getElementById("closemodel");
          btnClose.click();

          // Toast("success", "List successfully!");
        } else {
          Toast("error", "Error while uploading");
        }
      })
      .catch((err) => {
        CustomErrorResponse(err);
      });
  }

  function downloadDesign() {
    const svg = svgRef.current;
    if (!svg) return;

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);

    // Make sure XML header is included
    const svgBlob = new Blob(
      [`<?xml version="1.0" encoding="UTF-8"?>\n` + source],
      { type: "image/svg+xml;charset=utf-8" }
    );
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      // Export as PNG
      canvas.toBlob((blob) => {
        const jpgUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = jpgUrl;
        a.download = "polyhouse-foundation.jpg";
        a.click();
        URL.revokeObjectURL(jpgUrl);
      });

      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  function downloadCSV() {
    const dec = clamp(precision, 0, 6);
    const rows = [
      ["Label", "X (m)", "Y (m)", "Row", "Column"],
      ...points.map((p) => [
        p.label,
        p.x.toFixed(dec),
        p.y.toFixed(dec),
        p.row,
        String(p.col),
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `polyhouse-foundation-points.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const footingPx = Math.max(3, (footingDiaM * scale) / 2); // radius

  // ===== DEV SELF-TESTS =====
  useEffect(() => {
    try {
      // Spacing → counts
      const xsTest = buildAxisPositions(40, 4); // 0..40 by 4 => 11 grid lines, 10 blocks across
      const ysTest = buildAxisPositions(24, 8); // 0..24 by 8 => 4 grid lines, 3 blocks high
      console.assert(
        xsTest.length === 11,
        `Expected 11 columns, got ${xsTest.length}`
      );
      console.assert(
        ysTest.length === 4,
        `Expected 4 rows, got ${ysTest.length}`
      );

      // Allocation math & contiguity on current grid
      const total = blocks.length;
      const testPcts = [0, 25, 50, 75, 100];
      testPcts.forEach((p) => {
        const expectedFlat = Math.floor((p / 100) * total);
        // Build prefix set from serpentine order
        const prefix = new Set(serpentineOrder.slice(0, expectedFlat));

        // Ensure prefix size matches
        console.assert(
          prefix.size === expectedFlat,
          `Flatbed prefix size mismatch for ${p}%`
        );

        // For small totals, spot-check a few indices are within bounds
        prefix.forEach((idx) =>
          console.assert(
            idx >= 0 && idx < total,
            `Index OOB in prefix for ${p}%`
          )
        );
      });

      // Table placement inside a canonical 4×8 block
      const mockB = {
        x: 2,
        y: 4,
        name: "mock",
        x0: 0,
        y0: 0,
        w: 4,
        h: 8,
        xi: 0,
        yi: 0,
      };
      const { r1, r2, gapX, gapY } = computeTablesInBlock(mockB);
      const approx = (a, b) => Math.abs(a - b) < 1e-6;
      console.assert(approx(gapX, (4 - 2 * 1.2) / 3), `gapX wrong: ${gapX}`);
      console.assert(approx(gapY, (8 - 6.5) / 2), `gapY wrong: ${gapY}`);
      console.assert(approx(r1.w, 1.2) && approx(r2.w, 1.2), `widths wrong`);
      console.assert(approx(r1.h, 6.5) && approx(r2.h, 6.5), `heights wrong`);
      console.assert(approx(r2.x0 - (r1.x0 + r1.w), gapX), `middle gap wrong`);

      // Lines distribution sanity for a table 1.2 m wide
      const { xs: lineXs, step } = computeLineXs(r1.x0, r1.w, 12);
      console.assert(
        lineXs.length === 12,
        `expected 12 lines, got ${lineXs.length}`
      );
      console.assert(
        approx(lineXs[0], r1.x0 + r1.w / 13),
        `first line offset wrong`
      );
      console.assert(
        approx(lineXs[11], r1.x0 + (12 * r1.w) / 13),
        `last line offset wrong`
      );
      for (let i = 1; i < lineXs.length; i++)
        console.assert(
          approx(lineXs[i] - lineXs[i - 1], step),
          `spacing mismatch at ${i}`
        );

      // Ensure the requested 7.0 m lines exceed a 6.5 m table (so clipping is meaningful)
      const yStart = r1.y0 + (r1.h - 7.0) / 2;
      const yEnd = yStart + 7.0;
      console.assert(
        yStart < r1.y0 && yEnd > r1.y0 + r1.h,
        `lines should overhang table to trigger clip`
      );

      // Growbag rows tests
      const { ys: gYs, gap: gGap } = computeGrowbagLineYs(mockB);
      console.assert(
        gYs.length === 12,
        `Growbag lines expected 12, got ${gYs.length}`
      );
      // Check intra-pair gap and even spacing between pairs
      for (let p = 0; p < 6; p++) {
        const a = gYs[2 * p],
          b = gYs[2 * p + 1];
        console.assert(
          approx(b - a, GB_INTRA_PAIR_GAP),
          `intra-pair gap wrong at pair ${p}`
        );
      }
      for (let p = 0; p < 5; p++) {
        const firstNext = gYs[2 * (p + 1)];
        const firstCurr = gYs[2 * p];
        console.assert(
          approx(firstNext - firstCurr, GB_INTRA_PAIR_GAP + gGap),
          `pair spacing wrong at step ${p}`
        );
      }

      // Growbag pots tests: each row has 13 pots across 4 m with equal margins
      const { xs: potXs, step: potStep } = computeLineXs(
        0,
        Math.min(GB_LINE_LEN, mockB.w),
        GB_POTS_PER_LINE
      );
      console.assert(
        potXs.length === GB_POTS_PER_LINE,
        `pots per row expected ${GB_POTS_PER_LINE}, got ${potXs.length}`
      );
      console.assert(
        approx(
          potXs[0],
          Math.min(GB_LINE_LEN, mockB.w) / (GB_POTS_PER_LINE + 1)
        ),
        `first pot offset wrong`
      );
      for (let k = 1; k < potXs.length; k++)
        console.assert(
          approx(potXs[k] - potXs[k - 1], potStep),
          `pot spacing mismatch at ${k}`
        );
    } catch (e) {
      // no-op
    }
  }, [blocks, serpentineOrder]);

  function edgeOfHC(p) {
    const b = Number(bufferM) || 0;
    if (eq(p.y, -b)) return "bottom";
    if (eq(p.y, meta.W + b)) return "top";
    if (eq(p.x, -b)) return "left";
    return "right";
  }

  useEffect(() => {
    if (hcPairs) {
      setHCPairs(hcPairs);
    }
  }, [hcPairs]);

  return (
    <div className="w-full h-full p-4 text-sm">
      <h3>Polyhouse Systems Design</h3>
      <h4>{blockName?.join(" + ").toUpperCase()}</h4>
      <div className="bg-white rounded-2xl shadow p-4 border overflow-auto">
        {/* Responsive width: occupy 100% width; scroll when larger */}

        <div style={{ minWidth: "100%", width: canvasPx.w }}>
          <svg
            ref={svgRef}
            width="100%"
            height={canvasPx.h}
            viewBox={`0 0 ${canvasPx.w} ${canvasPx.h}`}
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Background */}
            <rect
              x={0}
              y={0}
              width={canvasPx.w}
              height={canvasPx.h}
              fill="#ffffff"
            />

            {/* Hockey Area (buffer each side) */}
            <rect
              x={mx(-bufferM)}
              y={my(widthM + bufferM)}
              width={(lengthM + 2 * bufferM) * scale}
              height={(widthM + 2 * bufferM) * scale}
              fill="#e0f2fe"
              fillOpacity={0.25}
              stroke="#0ea5e9"
              strokeWidth={1.5}
            />

            {/* Outline */}
            {showOutline && (
              <rect
                x={mx(0)}
                y={my(widthM)}
                width={lengthM * scale}
                height={widthM * scale}
                fill="none"
                stroke="#111"
                strokeWidth={1.5}
              />
            )}

            {/* Grid lines */}
            {showGrid && (
              <g>
                {xs.map((x, i) => (
                  <line
                    key={`vx-${i}`}
                    x1={mx(x)}
                    y1={my(0)}
                    x2={mx(x)}
                    y2={my(widthM)}
                    stroke="#e5e7eb"
                    strokeWidth={1}
                  />
                ))}
                {ys.map((y, i) => (
                  <line
                    key={`hy-${i}`}
                    x1={mx(0)}
                    y1={my(y)}
                    x2={mx(lengthM)}
                    y2={my(y)}
                    stroke="#e5e7eb"
                    strokeWidth={1}
                  />
                ))}
              </g>
            )}

            {/* Footings (foundation grid) - circles only */}
            <g>
              {points.map((p, idx) => (
                <circle
                  key={`f-${idx}`}
                  cx={mx(p.x)}
                  cy={my(p.y)}
                  r={footingPx}
                  fill="#111827"
                  opacity={0.95}
                />
              ))}
            </g>

            {/* Blocks (border + fill by assignment) */}
            <g>
              {blocks.map((b, i) => {
                const type = assignment.typeOf(i);
                let fill, typeName;

                switch (type) {
                  case "flat":
                    fill = "#c7d2fe"; // indigo
                    typeName = "Flatbed Block";
                    break;
                  case "growbag":
                    fill = "#fde68a"; // amber
                    typeName = "Growbag Block";
                    break;
                  case "dutch":
                    fill = "#6ee7b7"; // teal/light green for Dutch Bucket
                    typeName = "Dutch Bucket Block";
                    break;
                  case "aframe":
                  default:
                    fill = "#ddd6fe"; // purple (A-frame)
                    typeName = "A-frame Block";
                    break;
                }

                const tables =
                  type === "flat" || type === "aframe"
                    ? computeTablesInBlock(b)
                    : null;

                return (
                  <g key={`blk-${i}`}>
                    {/* block shell */}
                    <rect
                      x={mx(b.x0)}
                      y={my(b.y0 + b.h)}
                      width={b.w * scale}
                      height={b.h * scale}
                      fill={fill}
                      fillOpacity={0.6}
                      stroke="#4b5563"
                      strokeWidth={1.2}
                    />

                    {/* Flatbed internal tables + lines */}
                    {type === "flat" && tables && (
                      <>
                        {/* Table rectangles */}
                        <rect
                          x={mx(tables.r1.x0)}
                          y={my(tables.r1.y0 + tables.r1.h)}
                          width={tables.r1.w * scale}
                          height={tables.r1.h * scale}
                          fill="#f9fafb"
                          fillOpacity={0.9}
                          stroke="#1d4ed8"
                          strokeWidth={1.2}
                        />
                        <rect
                          x={mx(tables.r2.x0)}
                          y={my(tables.r2.y0 + tables.r2.h)}
                          width={tables.r2.w * scale}
                          height={tables.r2.h * scale}
                          fill="#f9fafb"
                          fillOpacity={0.9}
                          stroke="#1d4ed8"
                          strokeWidth={1.2}
                        />

                        {/* ClipPaths and lines */}
                        <defs>
                          <clipPath
                            id={`clip-r1-${i}`}
                            clipPathUnits="userSpaceOnUse"
                          >
                            <rect
                              x={mx(tables.r1.x0)}
                              y={my(tables.r1.y0 + tables.r1.h)}
                              width={tables.r1.w * scale}
                              height={tables.r1.h * scale}
                            />
                          </clipPath>
                          <clipPath
                            id={`clip-r2-${i}`}
                            clipPathUnits="userSpaceOnUse"
                          >
                            <rect
                              x={mx(tables.r2.x0)}
                              y={my(tables.r2.y0 + tables.r2.h)}
                              width={tables.r2.w * scale}
                              height={tables.r2.h * scale}
                            />
                          </clipPath>
                        </defs>

                        {/* Lines for r1 */}
                        {(() => {
                          const { xs: lineXs } = computeLineXs(
                            tables.r1.x0,
                            tables.r1.w,
                            LINES_PER_TABLE
                          );
                          const yStart =
                            tables.r1.y0 + (tables.r1.h - LINE_LEN) / 2;
                          const yEnd = yStart + LINE_LEN;
                          return (
                            <g clipPath={`url(#clip-r1-${i})`}>
                              {lineXs.map((xmR, k) => (
                                <line
                                  key={`r1-line-${k}`}
                                  x1={mx(xmR)}
                                  y1={my(yStart)}
                                  x2={mx(xmR)}
                                  y2={my(yEnd)}
                                  stroke="#1d4ed8"
                                  strokeWidth={0.8}
                                />
                              ))}
                            </g>
                          );
                        })()}

                        {/* Lines for r2 */}
                        {(() => {
                          const { xs: lineXs } = computeLineXs(
                            tables.r2.x0,
                            tables.r2.w,
                            LINES_PER_TABLE
                          );
                          const yStart =
                            tables.r2.y0 + (tables.r2.h - LINE_LEN) / 2;
                          const yEnd = yStart + LINE_LEN;
                          return (
                            <g clipPath={`url(#clip-r2-${i})`}>
                              {lineXs.map((xmR, k) => (
                                <line
                                  key={`r2-line-${k}`}
                                  x1={mx(xmR)}
                                  y1={my(yStart)}
                                  x2={mx(xmR)}
                                  y2={my(yEnd)}
                                  stroke="#1d4ed8"
                                  strokeWidth={0.8}
                                />
                              ))}
                            </g>
                          );
                        })()}
                      </>
                    )}

                    {/* Growbag internal: 12 rows (6 pairs), each 4 m long */}
                    {type === "growbag" &&
                      (() => {
                        const { ys: gy } = computeGrowbagLineYs(b);
                        const w = Math.min(GB_LINE_LEN, b.w);
                        const { xs: potXs } = computeLineXs(
                          b.x0,
                          w,
                          GB_POTS_PER_LINE
                        );
                        const rPx = Math.max(1.5, (GB_POT_D / 2) * scale);
                        return (
                          <g>
                            {gy.map((y, rowIdx) => (
                              <g key={`gb-row-${i}-${rowIdx}`}>
                                {potXs.map((cx, colIdx) => (
                                  <circle
                                    key={`gb-pot-${i}-${rowIdx}-${colIdx}`}
                                    cx={mx(cx)}
                                    cy={my(y)}
                                    r={rPx}
                                    fill="#fef3c7"
                                    stroke="#92400e"
                                    strokeWidth={0.8}
                                  />
                                ))}
                              </g>
                            ))}
                          </g>
                        );
                      })()}

                    {/* Dutch Bucket internal: simple 4 rows, 6 buckets each */}
                    {/* Dutch Bucket internal: squares like growbag */}
                    {type === "dutch" &&
                      (() => {
                        const { ys: gy } = computeGrowbagLineYs(b); // reuse growbag row positions
                        const w = Math.min(GB_LINE_LEN, b.w);
                        const { xs: potXs } = computeLineXs(
                          b.x0,
                          w,
                          GB_POTS_PER_LINE
                        );
                        const sizePx = Math.max(3, DB_POT_D * scale); // size of the square

                        return (
                          <g>
                            {gy.map((y, rowIdx) => (
                              <g key={`db-row-${i}-${rowIdx}`}>
                                {potXs.map((cx, colIdx) => (
                                  <rect
                                    key={`db-pot-${i}-${rowIdx}-${colIdx}`}
                                    x={mx(cx) - sizePx / 2}
                                    y={my(y) - sizePx / 2}
                                    width={sizePx}
                                    height={sizePx}
                                    fill="#d1fae5"
                                    stroke="#047857"
                                    strokeWidth={0.8}
                                  />
                                ))}
                              </g>
                            ))}
                          </g>
                        );
                      })()}

                    {/* A-frame rendering */}
                    {/* A-frame rendering */}
                    {type === "aframe" && tables && (
                      <>
                        {/* Table rectangles */}
                        <rect
                          x={mx(tables.r1.x0)}
                          y={my(tables.r1.y0 + tables.r1.h)}
                          width={tables.r1.w * scale}
                          height={tables.r1.h * scale}
                          fill="#f9fafb"
                          fillOpacity={0.9}
                          stroke="#dc2626"
                          strokeWidth={1.2}
                        />
                        <rect
                          x={mx(tables.r2.x0)}
                          y={my(tables.r2.y0 + tables.r2.h)}
                          width={tables.r2.w * scale}
                          height={tables.r2.h * scale}
                          fill="#f9fafb"
                          fillOpacity={0.9}
                          stroke="#dc2626"
                          strokeWidth={1.2}
                        />

                        {/* ClipPaths */}
                        <defs>
                          <clipPath
                            id={`clip-af1-${i}`}
                            clipPathUnits="userSpaceOnUse"
                          >
                            <rect
                              x={mx(tables.r1.x0)}
                              y={my(tables.r1.y0 + tables.r1.h)}
                              width={tables.r1.w * scale}
                              height={tables.r1.h * scale}
                            />
                          </clipPath>
                          <clipPath
                            id={`clip-af2-${i}`}
                            clipPathUnits="userSpaceOnUse"
                          >
                            <rect
                              x={mx(tables.r2.x0)}
                              y={my(tables.r2.y0 + tables.r2.h)}
                              width={tables.r2.w * scale}
                              height={tables.r2.h * scale}
                            />
                          </clipPath>
                        </defs>

                        {/* Vertical triangles for r1 */}
                        {(() => {
                          const parts = LINES_PER_TABLE; // how many triangles vertically
                          const segmentH = tables.r1.h / parts;
                          return (
                            <g clipPath={`url(#clip-af1-${i})`}>
                              {Array.from({ length: parts }).map((_, k) => {
                                const yTop = tables.r1.y0 + k * segmentH;
                                const yBottom = yTop + segmentH;
                                const xMid = tables.r1.x0 + tables.r1.w / 2;
                                return (
                                  <polygon
                                    key={`r1-tri-${k}`}
                                    points={`
                                      ${mx(xMid)},${my(yTop)} 
                                      ${mx(tables.r1.x0)},${my(yBottom)} 
                                      ${mx(tables.r1.x0 + tables.r1.w)},${my(
                                      yBottom
                                    )}
                                    `}
                                    fill="#56ba9f"
                                    fillOpacity={0.7}
                                  />
                                );
                              })}
                            </g>
                          );
                        })()}

                        {/* Vertical triangles for r2 */}
                        {(() => {
                          const parts = LINES_PER_TABLE;
                          const segmentH = tables.r2.h / parts;
                          return (
                            <g clipPath={`url(#clip-af2-${i})`}>
                              {Array.from({ length: parts }).map((_, k) => {
                                const yTop = tables.r2.y0 + k * segmentH;
                                const yBottom = yTop + segmentH;
                                const xMid = tables.r2.x0 + tables.r2.w / 2;
                                return (
                                  <polygon
                                    key={`r2-tri-${k}`}
                                    points={`
                  ${mx(xMid)},${my(yTop)} 
                  ${mx(tables.r2.x0)},${my(yBottom)} 
                  ${mx(tables.r2.x0 + tables.r2.w)},${my(yBottom)}
                `}
                                    fill="#56ba9f"
                                    fillOpacity={0.7}
                                  />
                                );
                              })}
                            </g>
                          );
                        })()}
                      </>
                    )}

                    {/* Labels */}
                    {showLabels && (
                      <>
                        <text
                          x={mx(b.x)}
                          y={my(b.y0 + b.h - 0.25)}
                          fontSize={scale / 3}
                          textAnchor="middle"
                          fill="#111827"
                        >
                          {b.name}
                        </text>
                        <text
                          x={mx(b.x)}
                          y={my(b.y0 + 0.25)}
                          fontSize={scale / 3}
                          textAnchor="middle"
                          fill="#111827"
                        >
                          {typeName}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}
            </g>

            {/* BC points (no labels) */}
            {showBC && (
              <g>
                {bcPoints.map((p, i) => (
                  <circle
                    key={`bc-${i}`}
                    cx={mx(p.x)}
                    cy={my(p.y)}
                    r={Math.max(3, footingPx * 0.7)}
                    fill="#f59e0b"
                    stroke="#111827"
                    strokeWidth={0.8}
                  />
                ))}
              </g>
            )}

            {/* HC points (no labels) */}
            {showHC && (
              <g>
                {hcPoints.map((p, i) => (
                  <rect
                    key={`hc-${i}`}
                    x={mx(p.x) - Math.max(3, footingPx * 0.6)}
                    y={my(p.y) - Math.max(3, footingPx * 0.6)}
                    width={Math.max(6, footingPx * 1.2)}
                    height={Math.max(6, footingPx * 1.2)}
                    fill="#0ea5e9"
                    stroke="#0c4a6e"
                    strokeWidth={0.8}
                  />
                ))}

                {/* Corner join lines */}
                {/* bottom-left */}
                <line
                  x1={mx(-bufferM + 2)}
                  y1={my(-bufferM)}
                  x2={mx(-bufferM)}
                  y2={my(-bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                <line
                  x1={mx(-bufferM)}
                  y1={my(-bufferM + 2)}
                  x2={mx(-bufferM)}
                  y2={my(-bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                {/* bottom-right */}
                <line
                  x1={mx(lengthM + bufferM - 2)}
                  y1={my(-bufferM)}
                  x2={mx(lengthM + bufferM)}
                  y2={my(-bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                <line
                  x1={mx(lengthM + bufferM)}
                  y1={my(-bufferM + 2)}
                  x2={mx(lengthM + bufferM)}
                  y2={my(-bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                {/* top-left */}
                <line
                  x1={mx(-bufferM + 2)}
                  y1={my(widthM + bufferM)}
                  x2={mx(-bufferM)}
                  y2={my(widthM + bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                <line
                  x1={mx(-bufferM)}
                  y1={my(widthM + bufferM - 2)}
                  x2={mx(-bufferM)}
                  y2={my(widthM + bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                {/* top-right */}
                <line
                  x1={mx(lengthM + bufferM - 2)}
                  y1={my(widthM + bufferM)}
                  x2={mx(lengthM + bufferM)}
                  y2={my(widthM + bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
                <line
                  x1={mx(lengthM + bufferM)}
                  y1={my(widthM + bufferM - 2)}
                  x2={mx(lengthM + bufferM)}
                  y2={my(widthM + bufferM)}
                  stroke="#0ea5e9"
                  strokeWidth={1.2}
                />
              </g>
            )}

            {/* Entry Cabin (4×4 m) between HCn and HC(n+1), centered on hockey border) */}
            {entryRect && (
              <>
                <rect
                  x={mx(entryRect.x0)}
                  y={my(entryRect.y0 + entryRect.h)}
                  width={entryRect.w * scale}
                  height={entryRect.h * scale}
                  fill="#10b981"
                  fillOpacity={0.6}
                  stroke="#065f46"
                  strokeWidth={1.2}
                />
                <text
                  x={mx(entryRect.x0) + (entryRect.w * scale) / 2}
                  y={my(entryRect.y0 + entryRect.h) + (entryRect.h * scale) / 2}
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  fontSize={scale / 2.5}
                  fontWeight="bold"
                  fill="#348c76"
                >
                  Entry Cabin
                </text>
              </>
            )}

            {/* ================= AXIS LABELS ONLY ================= */}

            {/* X Axis - LENGTH */}
            <text
              x={canvasPx.w / 2}
              y={canvasPx.h - 6}
              textAnchor="middle"
              fontSize={scale / 2}
              fontWeight="bold"
              fill="#111827"
            >
              LENGTH → {length} MTR
            </text>

            {/* Y Axis - WIDTH */}
            <text
              x={-canvasPx.h / 2}
              y={14}
              transform="rotate(-90)"
              textAnchor="middle"
              fontSize={scale / 2}
              fontWeight="bold"
              fill="#111827"
            >
              WIDTH → {width} MTR
            </text>
          </svg>
        </div>
      </div>

      <button className="btn btn-primary m-2 btn-sm" onClick={downloadDesign}>
        Download
      </button>
      {role !== "saleStaff" && (
        <button
          className="btn btn-primary m-2 btn-sm"
          data-bs-toggle="modal"
          data-bs-target="#combinedList"
          // style={{ display: "none" }}
          id="openmodel"
        >
          Generate Combined Material List (Flatbed + Growbag)
        </button>
      )}

      <div
        className="modal modal-blur fade"
        id="combinedList"
        tabIndex="-1"
        aria-hidden="true"
        // style={{ display: "none" }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Combined Growbag + Flatbed List</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="closemodel"
              ></button>
            </div>
            <div className="mb-3 p-3">
              <label className="form-label fw-semibold">Client Name</label>

              <input
                type="text"
                className="form-control"
                placeholder="Enter client name"
                onChange={(e) => setClientName(e.target.value)}
              />

              <button
                className="btn btn-primary mt-2"
                onClick={() => calculateSystemSize(blocks, assignment)}
              >
                Generate Pdf
              </button>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn me-auto"
                data-bs-dismiss="modal"
                id="closemodel"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
